import React from "react";
import { ColDef } from "ag-grid-community";
import Document from "../../../models/document";

interface FileColumnParams {
  changedCells: { [key: number]: Set<string> };
  downloadingIds: Set<number>;
  previewingIds: Set<number>;
  setChangedCells: React.Dispatch<
    React.SetStateAction<{ [key: number]: Set<string> }>
  >;
  markCellChanged: (rowId: number, field?: string) => void;
  isPdfFile: (filePath?: string | null) => boolean;
  handleDownloadClick: (downloadDoc: Document) => void;
  handlePreviewPdfClick: (previewDoc: Document) => void;
  handleFileUpload: (
    file: File,
    documentId: number | undefined,
    documentData: Document
  ) => Promise<boolean>;
}

const getFileIcon = (hasFile: boolean, fileExtension?: string) => {
  if (!hasFile) return "fa fa-file";

  switch (fileExtension) {
    case "pdf":
      return "fa fa-file-pdf";
    case "doc":
    case "docx":
      return "fa fa-file-word";
    case "xls":
    case "xlsx":
      return "fa fa-file-excel";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "fa fa-file-image";
    default:
      return "fa fa-file";
  }
};

export const buildFileColumns = ({
  changedCells,
  downloadingIds,
  previewingIds,
  setChangedCells,
  markCellChanged,
  isPdfFile,
  handleDownloadClick,
  handlePreviewPdfClick,
  handleFileUpload,
}: FileColumnParams): ColDef[] => [
  {
    field: "filePath",
    headerName: "Archivo adjunto",
    filter: true,
    cellClassRules: {
      "cell-changed": (params) =>
        params.data && changedCells[params.data.id]?.has(params.colDef.field ?? ""),
    },
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const downloadingDocument: Document = params.data;
      const filePath = params.value;
      const hasFile =
        filePath && typeof filePath === "string" && filePath.trim() !== "";
      const fileName = hasFile ? filePath.split("/").pop() || "" : "Sin archivo";
      const fileExtension = hasFile
        ? fileName.split(".").pop()?.toLowerCase()
        : "";

      const isDownloading = downloadingIds.has(downloadingDocument.id!);
      const isPreviewing = previewingIds.has(downloadingDocument.id!);
      const canPreviewPdf =
        hasFile &&
        isPdfFile(filePath) &&
        downloadingDocument.id !== undefined &&
        downloadingDocument.id > 0;

      const handleDownload = (
        documentToDownload: Document,
        e: React.MouseEvent
      ) => {
        e.preventDefault();
        if (!hasFile || isDownloading) {
          return;
        }
        handleDownloadClick(documentToDownload);
      };

      const handlePreview = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!canPreviewPdf || isPreviewing) {
          return;
        }
        handlePreviewPdfClick(downloadingDocument);
      };

      return (
        <div
          onClick={(e: React.MouseEvent) =>
            handleDownload(downloadingDocument, e)
          }
          style={{
            cursor: hasFile && !isDownloading ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "5px",
            opacity: isDownloading ? 0.7 : 1,
          }}
          title={
            isDownloading
              ? "Descargando..."
              : hasFile
              ? `Descargar ${fileName}`
              : "No hay archivo disponible"
          }
        >
          {canPreviewPdf && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handlePreview}
              disabled={isPreviewing}
              title="Previsualizar PDF"
              style={{
                padding: "2px 6px",
                lineHeight: 1.2,
                flexShrink: 0,
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0, 123, 255, 0.35)",
              }}
            >
              <i
                className={isPreviewing ? "fa fa-spinner fa-spin" : "fa fa-eye"}
              ></i>
            </button>
          )}
          <i
            className={`${
              isDownloading
                ? "fa fa-spinner fa-spin"
                : getFileIcon(Boolean(hasFile), fileExtension)
            } ${hasFile ? "text-primary" : "text-secondary"}`}
          />
          <span
            style={{
              color: hasFile ? "#007bff" : "#6c757d",
              textDecoration: hasFile ? "underline" : "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "150px",
            }}
          >
            {isDownloading ? "Descargando..." : fileName}
          </span>
        </div>
      );
    },
  },
  {
    field: "filePath",
    filter: false,
    sortable: false,
    resizable: false,
    headerName: "Subir/Actualizar archivo",
    width: 220,
    cellRenderer: (params: any) => {
      if (!params.data) return null;
      const filePath = params.data?.filePath;
      const hasFile =
        filePath && typeof filePath === "string" && filePath.trim() !== "";

      const handleFileInputChange = async (
        e: React.ChangeEvent<HTMLInputElement>
      ) => {
        if (e.target.files && e.target.files.length > 0) {
          const file = e.target.files[0];
          const documentId = params.data.id;
          const documentData = params.data;
          const success = await handleFileUpload(file, documentId, documentData);
          if (success) {
            params.node.setDataValue("filePath", file.name);
            setChangedCells((prev: any) => {
              const newChanges = { ...prev };
              if (!newChanges[documentId]) {
                newChanges[documentId] = new Set();
              }
              newChanges[documentId].add("filePath");
              return newChanges;
            });
          }
        }
      };

      const handleClearFile = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!hasFile) {
          return;
        }

        if (
          !window.confirm(
            "¿Está seguro que desea quitar el archivo de este documento?"
          )
        ) {
          return;
        }

        params.node.setDataValue("filePath", "");
        params.node.setDataValue("version", undefined);
        params.data.filePath = "";
        params.data.version = undefined;
        markCellChanged(params.data.id, "filePath");
      };

      return (
        <div
          style={{
            display: "flex",
            gap: "6px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <label
            className="btn btn-outline-primary btn-sm"
            style={{
              padding: "3px 6px",
              minWidth: "30px",
              cursor: "pointer",
              margin: "0",
            }}
          >
            <i className="fa fa-upload"></i>Cargar archivo
            <input
              type="file"
              onChange={handleFileInputChange}
              style={{
                display: "none",
              }}
            />
          </label>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={handleClearFile}
            disabled={!hasFile}
            title={
              hasFile
                ? "Quitar archivo del documento"
                : "No hay archivo para quitar"
            }
            style={{
              padding: "3px 6px",
              minWidth: "30px",
              opacity: hasFile ? 1 : 0.5,
            }}
          >
            <i className="fa fa-times"></i> Limpiar
          </button>
        </div>
      );
    },
  },
];
